import { useState, useCallback } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2'
import AddIcon from '@mui/icons-material/Add'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import DeleteIcon from '@mui/icons-material/Delete'
import LockIcon from '@mui/icons-material/Lock'
import { v4 as uuidv4 } from 'uuid'
import { FormFieldSchema, FormSchema, FormRowSchema, FormRowItem, FieldType } from '../../common/types/FormSchema'
import { useFormBuilderStyles } from './FormBuilder.styles'

const toFieldName = (label: string) =>
  label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')

interface FormBuilderProps {
  initialSchema?: FormSchema
  initialOdooNumber?: number
  initialProductName?: string
  onSave: (data: { id: string; schema: FormSchema; odooNumber: number; productName: string }) => void
  isLoading?: boolean
  readOnlyOdoo?: boolean
}

const createField = (type: FieldType = 'text'): FormFieldSchema => ({
  id: uuidv4(),
  type,
  label: '',
  name: `campo_${uuidv4().slice(0, 8)}`,
  required: false,
  gridSize: 12,
  options: type === 'select' || type === 'multiselect' || type === 'radio' ? [{ label: '', value: '' }] : undefined,
})

const distributeWidth = (itemCount: number): number => {
  if (itemCount >= 4) return 3
  if (itemCount === 0) return 12
  return Math.floor(12 / itemCount)
}

const createItem = (): FormRowItem => ({
  field: createField(),
  width: 12,
})

const createRow = (): FormRowSchema => {
  const items = [createItem()]
  const width = distributeWidth(items.length)
  return { id: uuidv4(), items: items.map(i => ({ ...i, width })) }
}

const SECTION_IDS = {
  PATIENT: 'section-patient',
  SAMPLE: 'section-sample',
}

const SECTION_TITLES: Record<string, string> = {
  [SECTION_IDS.PATIENT]: 'Datos del paciente',
  [SECTION_IDS.SAMPLE]: 'Datos de la muestra',
}

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'Texto' },
  { value: 'number', label: 'Número' },
  { value: 'email', label: 'Email' },
  { value: 'textarea', label: 'Área de texto' },
  { value: 'select', label: 'Selección única' },
  { value: 'multiselect', label: 'Selección múltiple' },
  { value: 'radio', label: 'Radio' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Fecha' },
  { value: 'file', label: 'Archivo' },
]

const FIELD_BASE = { gridSize: 6, required: true }
const SAMPLE_TYPE_BASE = { gridSize: 12, required: true }

const PATIENT_FIXED_ROWS: FormRowSchema[] = [
  {
    id: 'fixed-row-patient-1',
    items: [
      { field: { id: 'fixed-patient-first_name', type: 'text', label: 'Nombre', name: 'first_name', ...FIELD_BASE }, width: 6 },
      { field: { id: 'fixed-patient-last_name', type: 'text', label: 'Apellidos', name: 'last_name', ...FIELD_BASE }, width: 6 },
    ],
  },
  {
    id: 'fixed-row-patient-2',
    items: [
      { field: { id: 'fixed-patient-dni_nhc', type: 'text', label: 'DNI / NHC', name: 'dni_nhc', ...FIELD_BASE }, width: 6 },
      { field: { id: 'fixed-patient-date_of_birth', type: 'date', label: 'Fecha de nacimiento', name: 'date_of_birth', ...FIELD_BASE }, width: 6 },
    ],
  },
]

const SAMPLE_FIXED_ROWS: FormRowSchema[] = [

  {
    id: 'fixed-row-sample-1',
    items: [
      { field: { id: 'fixed-sample-sample_code', type: 'text', label: 'Código de la muestra', name: 'sample_code', ...FIELD_BASE }, width: 6 },
      { field: { id: 'fixed-sample-extraction_date', type: 'date', label: 'Fecha de extracción', name: 'extraction_date', ...FIELD_BASE }, width: 6 },
    ],
  },
]

const deepCloneRows = (rows: FormRowSchema[]): FormRowSchema[] =>
  rows.map(r => ({
    ...r,
    items: r.items.map(item => ({
      ...item,
      field: { ...item.field, options: item.field.options ? item.field.options.map(o => ({ ...o })) : undefined },
    })),
  }))

type SectionConfig = {
  id: string
  title: string
  fixedRows: FormRowSchema[]
  customRows: FormRowSchema[]
}

const initSections = (initialSchema?: FormSchema): SectionConfig[] => {
  if (!initialSchema?.sections?.length) {
    return [
      { id: SECTION_IDS.PATIENT, title: SECTION_TITLES[SECTION_IDS.PATIENT], fixedRows: deepCloneRows(PATIENT_FIXED_ROWS), customRows: [] },
      { id: SECTION_IDS.SAMPLE, title: SECTION_TITLES[SECTION_IDS.SAMPLE], fixedRows: deepCloneRows(SAMPLE_FIXED_ROWS), customRows: [] },
    ]
  }
  return initialSchema.sections.map(s => {
    const isPatient = s.id === SECTION_IDS.PATIENT
    const allFixed = isPatient ? PATIENT_FIXED_ROWS : SAMPLE_FIXED_ROWS
    const fixedIds = new Set(allFixed.map(r => r.id))
    return {
      id: s.id,
      title: s.title,
      fixedRows: deepCloneRows(allFixed),
      customRows: s.rows.filter(r => !fixedIds.has(r.id)),
    }
  })
}

export default function FormBuilder({ initialSchema, initialOdooNumber, initialProductName, onSave, isLoading, readOnlyOdoo }: FormBuilderProps) {
  const classes = useFormBuilderStyles()
  const [odooNumber, setOdooNumber] = useState(initialOdooNumber ?? 0)
  const [productName, setProductName] = useState(initialProductName ?? initialSchema?.title ?? '')
  const [sections, setSections] = useState<SectionConfig[]>(() => initSections(initialSchema))
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [nameErrors, setNameErrors] = useState<Record<string, string>>({})

  /** Devuelve un mapa fieldId → mensaje de error para nombres duplicados dentro de una sección */
  const computeNameErrors = (secs: SectionConfig[]): Record<string, string> => {
    const errors: Record<string, string> = {}
    secs.forEach(sec => {
      const seen: Record<string, string[]> = {}
      sec.customRows.forEach(row => {
        row.items.forEach(item => {
          const name = item.field.name?.trim() ?? ''
          if (!name) {
            errors[item.field.id] = 'El nombre no puede estar vacío'
            return
          }
          if (!seen[name]) seen[name] = []
          seen[name].push(item.field.id)
        })
      })
      Object.entries(seen).forEach(([, ids]) => {
        if (ids.length > 1) {
          ids.forEach(id => { errors[id] = 'Nombre duplicado en este formulario' })
        }
      })
    })
    return errors
  }

  const updateItemField = useCallback((sectionIdx: number, rowIdx: number, itemIdx: number, patch: Partial<FormFieldSchema>) => {
    setSections(prev => {
      const next = [...prev]
      const rows = [...next[sectionIdx].customRows]
      const row = { ...rows[rowIdx] }
      const items = [...row.items]
      items[itemIdx] = { ...items[itemIdx], field: { ...items[itemIdx].field, ...patch } }
      row.items = items
      rows[rowIdx] = row
      next[sectionIdx] = { ...next[sectionIdx], customRows: rows }
      setNameErrors(computeNameErrors(next))
      return next
    })
  }, [])

  const updateFixedOption = useCallback((sectionIdx: number, optIdx: number, value: string) => {
    setSections(prev => {
      const next = [...prev]
      const rows = [...next[sectionIdx].fixedRows]
      const row = { ...rows[0] }
      const items = [...row.items]
      const item = { ...items[0] }
      const options = [...(item.field.options ?? [])]
      options[optIdx] = { label: value, value: toFieldName(value) || `opt_${optIdx}` }
      item.field = { ...item.field, options }
      items[0] = item
      row.items = items
      rows[0] = row
      next[sectionIdx] = { ...next[sectionIdx], fixedRows: rows }
      return next
    })
  }, [])

  const addFixedOption = useCallback((sectionIdx: number) => {
    setSections(prev => {
      const next = [...prev]
      const rows = [...next[sectionIdx].fixedRows]
      const row = { ...rows[0] }
      const items = [...row.items]
      const item = { ...items[0] }
      const options = [...(item.field.options ?? []), { label: '', value: '' }]
      item.field = { ...item.field, options }
      items[0] = item
      row.items = items
      rows[0] = row
      next[sectionIdx] = { ...next[sectionIdx], fixedRows: rows }
      return next
    })
  }, [])

  const removeFixedOption = useCallback((sectionIdx: number, optIdx: number) => {
    setSections(prev => {
      const next = [...prev]
      const rows = [...next[sectionIdx].fixedRows]
      const row = { ...rows[0] }
      const items = [...row.items]
      const item = { ...items[0] }
      const options = item.field.options?.filter((_, i) => i !== optIdx)
      item.field = { ...item.field, options }
      items[0] = item
      row.items = items
      rows[0] = row
      next[sectionIdx] = { ...next[sectionIdx], fixedRows: rows }
      return next
    })
  }, [])

  const updateFixedItemRequired = useCallback((sectionIdx: number, rowIdx: number, itemIdx: number, required: boolean) => {
    setSections(prev => {
      const next = [...prev]
      const rows = [...next[sectionIdx].fixedRows]
      const row = { ...rows[rowIdx] }
      const items = [...row.items]
      items[itemIdx] = { ...items[itemIdx], field: { ...items[itemIdx].field, required } }
      row.items = items
      rows[rowIdx] = row
      next[sectionIdx] = { ...next[sectionIdx], fixedRows: rows }
      return next
    })
  }, [])

  const addRow = useCallback((sectionIdx: number) => {
    setSections(prev => {
      const next = [...prev]
      next[sectionIdx] = { ...next[sectionIdx], customRows: [...next[sectionIdx].customRows, createRow()] }
      return next
    })
  }, [])

  const removeRow = useCallback((sectionIdx: number, rowIdx: number) => {
    setSections(prev => {
      const next = [...prev]
      next[sectionIdx] = { ...next[sectionIdx], customRows: next[sectionIdx].customRows.filter((_, i) => i !== rowIdx) }
      return next
    })
    setDeleteConfirm(null)
  }, [])

  const moveRow = useCallback((sectionIdx: number, rowIdx: number, direction: 'up' | 'down') => {
    setSections(prev => {
      const next = [...prev]
      const rows = [...next[sectionIdx].customRows]
      const targetIdx = direction === 'up' ? rowIdx - 1 : rowIdx + 1
      if (targetIdx < 0 || targetIdx >= rows.length) return prev
      ;[rows[rowIdx], rows[targetIdx]] = [rows[targetIdx], rows[rowIdx]]
      next[sectionIdx] = { ...next[sectionIdx], customRows: rows }
      return next
    })
  }, [])

  const addItemToRow = useCallback((sectionIdx: number, rowIdx: number) => {
    setSections(prev => {
      const next = [...prev]
      const rows = [...next[sectionIdx].customRows]
      const items = [...rows[rowIdx].items, createItem()]
      const w = distributeWidth(items.length)
      rows[rowIdx] = { ...rows[rowIdx], items: items.map(i => ({ ...i, width: w })) }
      next[sectionIdx] = { ...next[sectionIdx], customRows: rows }
      return next
    })
  }, [])

  const removeItemFromRow = useCallback((sectionIdx: number, rowIdx: number, itemIdx: number) => {
    setSections(prev => {
      const next = [...prev]
      const rows = [...next[sectionIdx].customRows]
      const remaining = rows[rowIdx].items.filter((_, i) => i !== itemIdx)
      if (remaining.length === 0) {
        next[sectionIdx] = { ...next[sectionIdx], customRows: rows.filter((_, i) => i !== rowIdx) }
      } else {
        const w = distributeWidth(remaining.length)
        rows[rowIdx] = { ...rows[rowIdx], items: remaining.map(i => ({ ...i, width: w })) }
        next[sectionIdx] = { ...next[sectionIdx], customRows: rows }
      }
      return next
    })
    setDeleteConfirm(null)
  }, [])

  const handleSave = () => {
    const errors = computeNameErrors(sections)
    setNameErrors(errors)
    if (Object.keys(errors).length > 0) return
    const formId = initialSchema?.id ?? uuidv4()
    const schema: FormSchema = {
      id: formId,
      title: productName,
      sections: sections.map(s => ({
        id: s.id,
        title: s.title,
        rows: [...s.fixedRows, ...s.customRows],
      })),
      createdAt: initialSchema?.createdAt ?? new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    onSave({ id: formId, schema, odooNumber, productName })
  }

  const renderFixedOptionsEditor = (sectionIdx: number) => {
    const lastFixedRow = sections[sectionIdx].fixedRows[sections[sectionIdx].fixedRows.length - 1]
    const sampleTypeItem = lastFixedRow?.items[0]
    const field = sampleTypeItem?.field

    if (!field) return null

    return (
      <Box className={classes.optionsBox}>
        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
          Opciones de tipo de muestra
        </Typography>
        {field.options?.map((opt, optIdx) => (
          <Box key={optIdx} display="flex" alignItems="center" gap={1} mt={0.5}>
            <TextField
              size="small"
              placeholder="Ej: Sangre, Tejido, Orina..."
              value={opt.label}
              onChange={e => updateFixedOption(sectionIdx, optIdx, e.target.value)}
              sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff' } }}
            />
            <IconButton size="small" color="error" onClick={() => removeFixedOption(sectionIdx, optIdx)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={() => addFixedOption(sectionIdx)} sx={{ mt: 0.5 }}>
          Añadir opción
        </Button>
      </Box>
    )
  }

  const renderFixedFieldPreview = (field: FormFieldSchema) => {
    switch (field.type) {
      case 'date':
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={field.label}
              disabled
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  sx: { '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff' } },
                },
              }}
            />
          </LocalizationProvider>
        )
      case 'select':
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select label={field.label} value="" disabled sx={{ backgroundColor: '#ffffff' }}>
              <MenuItem value="">
                <em>Seleccionar...</em>
              </MenuItem>
              {field.options?.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label || '(vacío)'}</MenuItem>
              ))}
            </Select>
            <FormHelperText>Campo Odoo: {field.name}</FormHelperText>
          </FormControl>
        )
      default:
        return (
          <TextField
            fullWidth
            size="small"
            label={field.label}
            placeholder={field.label}
            disabled
            helperText={`Campo Odoo: ${field.name}`}
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff' } }}
          />
        )
    }
  }

  const renderFixedItems = (row: FormRowSchema, sectionIdx: number, rowIdx: number) => (
    <Box>
      <Grid container spacing={2}>
        {row.items.map((item, itemIdx) => {
          const f = item.field
          const isSampleType = f.id === 'fixed-sample-sample_type'
          return (
            <Grid item xs={item.width} key={f.id}>
              <Box sx={{ position: 'relative' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {f.label}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={f.required}
                        onChange={e => updateFixedItemRequired(sectionIdx, rowIdx, itemIdx, e.target.checked)}
                        size="small"
                      />
                    }
                    label="Obligatorio"
                    sx={{ ml: 'auto', '& .MuiTypography-root': { fontSize: '0.75rem' } }}
                  />
                </Box>
                {renderFixedFieldPreview(f)}
                {isSampleType && (
                  <Box sx={{ mt: 1 }}>
                    {renderFixedOptionsEditor(sectionIdx)}
                  </Box>
                )}
              </Box>
            </Grid>
          )
        })}
      </Grid>
    </Box>
  )

  const typeChipLabel = (type: FieldType) => FIELD_TYPES.find(t => t.value === type)?.label ?? type

  return (
    <Box className={classes.root}>

      {/* ── Cabecera: nombre del producto + número Odoo ── */}
      <Box className={classes.headerFields}>
        <TextField
          fullWidth
          label="Nombre del producto"
          value={productName}
          onChange={e => setProductName(e.target.value)}
          variant="outlined"
          sx={{ flex: 9, '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff' } }}
        />
        <TextField
          fullWidth
          type="number"
          label="Nº Odoo"
          value={odooNumber}
          onChange={e => setOdooNumber(Number(e.target.value))}
          disabled={readOnlyOdoo}
          variant="outlined"
          sx={{ flex: 3, '& .MuiOutlinedInput-root': { backgroundColor: readOnlyOdoo ? '#f5f5f5' : '#ffffff' } }}
        />
      </Box>

      {sections.map((sec, sectionIdx) => (
        <Box key={sec.id} className={classes.sectionCard}>
          <Box className={classes.sectionHeader}>
            <Typography className={classes.sectionTitle}>
              {sec.title}
            </Typography>
          </Box>

          <Typography variant="subtitle2" className={classes.subsectionLabel}>
            Campos obligatorios
          </Typography>

          {sec.fixedRows.map((row, rowIdx) => (
            <Box key={row.id} className={classes.fixedRow}>
              {renderFixedItems(row, sectionIdx, rowIdx)}
            </Box>
          ))}

          <Divider className={classes.divider} />

          <Typography variant="subtitle2" className={classes.subsectionLabel}>
            Campos adicionales
          </Typography>

          {sec.customRows.length === 0 && (
            <Typography variant="body2" className={classes.emptyState}>
              No hay filas adicionales aún. Haz clic en "Añadir fila" para comenzar.
            </Typography>
          )}

          {sec.customRows.map((row, rowIdx) => (
            <Box key={row.id} className={classes.rowCard}>
              <Box className={classes.rowHeader}>
                <Typography variant="caption" className={classes.rowLabel}>
                  Fila {rowIdx + 1}
                </Typography>
                <Box className={classes.rowActions}>
                  <IconButton
                    size="small"
                    disabled={rowIdx === 0}
                    onClick={() => moveRow(sectionIdx, rowIdx, 'up')}
                    sx={{ color: '#757575' }}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={rowIdx === sec.customRows.length - 1}
                    onClick={() => moveRow(sectionIdx, rowIdx, 'down')}
                    sx={{ color: '#757575' }}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteConfirm(deleteConfirm === row.id ? null : row.id)}
                    sx={{ color: '#757575', '&:hover': { color: '#d32f2f' } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Box className={classes.itemsRow}>
                {row.items.map((item, itemIdx) => {
                  const f = item.field
                  return (
                    <Box key={f.id} className={classes.itemCard}>
                      <Box className={classes.itemHeader}>
                        <Chip
                          label={typeChipLabel(f.type)}
                          size="small"
                          sx={{
                            backgroundColor: '#00b8e2',
                            color: '#ffffff',
                            fontWeight: 600,
                            fontSize: '0.72rem',
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => setDeleteConfirm(deleteConfirm === f.id ? null : f.id)}
                          sx={{ color: '#757575', '&:hover': { color: '#d32f2f' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Box className={classes.itemFieldGroup}>
                        <Select
                          size="small"
                          value={f.type}
                          onChange={e => {
                            const newType = e.target.value as FieldType
                            const needsOptions = newType === 'select' || newType === 'multiselect' || newType === 'radio'
                            updateItemField(sectionIdx, rowIdx, itemIdx, {
                              type: newType,
                              options: needsOptions ? f.options ?? [{ label: '', value: '' }] : undefined,
                            })
                          }}
                          sx={{ bgcolor: '#ffffff' }}
                        >
                          {FIELD_TYPES.map(ft => (
                            <MenuItem key={ft.value} value={ft.value}>{ft.label}</MenuItem>
                          ))}
                        </Select>

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={f.required}
                              onChange={e => updateItemField(sectionIdx, rowIdx, itemIdx, { required: e.target.checked })}
                              size="small"
                            />
                          }
                          label="Obligatorio"
                          sx={{ ml: 0, '& .MuiTypography-root': { fontSize: '0.8125rem' } }}
                        />
                      </Box>

                      <TextField
                        fullWidth
                        size="small"
                        label="Etiqueta"
                        placeholder="Ej: Dirección"
                        value={f.label}
                        onChange={e => updateItemField(sectionIdx, rowIdx, itemIdx, { label: e.target.value })}
                        sx={{ mb: 0.75, '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff' } }}
                      />

                      <TextField
                        fullWidth
                        size="small"
                        label="Campo Odoo"
                        placeholder="Ej: x_direccion"
                        value={f.name}
                        error={!!nameErrors[f.id]}
                        helperText={nameErrors[f.id]}
                        onChange={e => updateItemField(sectionIdx, rowIdx, itemIdx, { name: e.target.value })}
                        sx={{ mb: 0.75, '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff' } }}
                      />

                      <TextField
                        fullWidth
                        size="small"
                        label="Texto de ayuda"
                        placeholder="Texto opcional"
                        value={f.helpText ?? ''}
                        onChange={e => updateItemField(sectionIdx, rowIdx, itemIdx, { helpText: e.target.value })}
                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff' } }}
                      />

                      {(f.type === 'select' || f.type === 'multiselect' || f.type === 'radio') && (
                        <Box className={classes.optionsBox} sx={{ mt: 1 }}>
                          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                            Opciones
                          </Typography>
                          {f.options?.map((opt, optIdx) => (
                            <Box key={optIdx} display="flex" alignItems="center" gap={0.5} mt={0.5}>
                              <TextField
                                size="small"
                                placeholder="Opción"
                                value={opt.label}
                                onChange={e => {
                                  const label = e.target.value
                                  setSections(prev => {
                                    const next = [...prev]
                                    const rows = [...next[sectionIdx].customRows]
                                    const row = { ...rows[rowIdx] }
                                    const items = [...row.items]
                                    const item = { ...items[itemIdx] }
                                    const fieldOpts = [...(item.field.options ?? [])]
                                    fieldOpts[optIdx] = { label, value: toFieldName(label) || `opt_${optIdx}` }
                                    item.field = { ...item.field, options: fieldOpts }
                                    items[itemIdx] = item
                                    row.items = items
                                    rows[rowIdx] = row
                                    next[sectionIdx] = { ...next[sectionIdx], customRows: rows }
                                    return next
                                  })
                                }}
                                sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff' } }}
                              />
                              <IconButton size="small" color="error" onClick={() => {
                                setSections(prev => {
                                  const next = [...prev]
                                  const rows = [...next[sectionIdx].customRows]
                                  const row = { ...rows[rowIdx] }
                                  const items = [...row.items]
                                  const item = { ...items[itemIdx] }
                                  const fieldOpts = item.field.options?.filter((_, i) => i !== optIdx)
                                  item.field = { ...item.field, options: fieldOpts }
                                  items[itemIdx] = item
                                  row.items = items
                                  rows[rowIdx] = row
                                  next[sectionIdx] = { ...next[sectionIdx], customRows: rows }
                                  return next
                                })
                              }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                          <Button size="small" startIcon={<AddIcon />} onClick={() => {
                            setSections(prev => {
                              const next = [...prev]
                              const rows = [...next[sectionIdx].customRows]
                              const row = { ...rows[rowIdx] }
                              const items = [...row.items]
                              const item = { ...items[itemIdx] }
                              const fieldOpts = [...(item.field.options ?? []), { label: '', value: '' }]
                              item.field = { ...item.field, options: fieldOpts }
                              items[itemIdx] = item
                              row.items = items
                              rows[rowIdx] = row
                              next[sectionIdx] = { ...next[sectionIdx], customRows: rows }
                              return next
                            })
                          }} sx={{ mt: 0.5 }}>
                            Añadir opción
                          </Button>
                        </Box>
                      )}

                      {deleteConfirm === f.id && (
                        <Box className={classes.deleteConfirm}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                            ¿Eliminar este campo?
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => removeItemFromRow(sectionIdx, rowIdx, itemIdx)}
                            sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' }, color: '#fff', ml: 'auto' }}
                          >
                            Sí
                          </Button>
                          <Button
                            size="small"
                            className={classes.buttonSecondary}
                            onClick={() => setDeleteConfirm(null)}
                          >
                            No
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )
                })}
              </Box>

              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => addItemToRow(sectionIdx, rowIdx)}
                sx={{ mt: 1, color: '#00b8e2', textTransform: 'none' }}
              >
                Añadir campo a esta fila
              </Button>

              {deleteConfirm === row.id && (
                <Box className={classes.deleteConfirm} sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                    ¿Eliminar toda la fila?
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => removeRow(sectionIdx, rowIdx)}
                    sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' }, color: '#fff', ml: 'auto' }}
                  >
                    Sí, eliminar
                  </Button>
                  <Button
                    size="small"
                    className={classes.buttonSecondary}
                    onClick={() => setDeleteConfirm(null)}
                  >
                    Cancelar
                  </Button>
                </Box>
              )}
            </Box>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => addRow(sectionIdx)}
            className={classes.addRowBtn}
          >
            Añadir fila
          </Button>
        </Box>
      ))}

      <Box className={classes.actions}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          disabled={isLoading || !productName.trim() || Object.keys(nameErrors).length > 0}
          className={classes.button}
        >
          {isLoading ? 'Guardando...' : initialSchema ? 'Actualizar formulario' : 'Guardar formulario'}
        </Button>
      </Box>
    </Box>
  )
}
